import { useLingui } from '@lingui/react';
import type { WorkflowTransitionResponse } from '@/types/workflow';
import { getActionLabel, getStateLabel, getStateTone } from '@/types/workflow';
import { StatusBadge } from '@/components/ui/status-badge';
import { formatDateTime } from '@/lib/format';

interface WorkflowTimelineProps {
  transitions: WorkflowTransitionResponse[];
  userNameMap?: Map<string, string>;
  /** The signed-in user's Clerk id: rows by this actor read in the second person ("You reminded ..."). */
  viewerUserId?: string | null;
  /** The item's category: names what closed a care alert item by itself. */
  category?: string;
}

export function WorkflowTimeline({ transitions, userNameMap, viewerUserId, category }: WorkflowTimelineProps) {
  const { i18n } = useLingui();

  if (!transitions || transitions.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        {i18n._({ id: 'workflow.timeline.emptyHistory', message: 'No history yet' })}
      </div>
    );
  }

  // Newest first
  const sorted = [...transitions].sort((a, b) => {
    const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return (Number.isNaN(timeB) ? 0 : timeB) - (Number.isNaN(timeA) ? 0 : timeA);
  });

  return (
    <div className="space-y-1">
      <h3 className="text-sm font-semibold mb-3">
        {i18n._({ id: 'workflow.timeline.title', message: 'Timeline' })}
      </h3>
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-3 top-2 bottom-2 w-px bg-border" aria-hidden="true" />

        <div className="space-y-4">
          {sorted.map((transition) => {
            const actor = transition.actor_id
              ? (userNameMap?.get(transition.actor_id) ?? transition.actor_id)
              : '';
            // An assignment row keeps the state: it says who took the item
            // instead of a from -> to pair of identical badges.
            const assignment = transition.action === 'assign' || transition.action === 'unassign';
            const reminder = transition.action === 'remind';
            const byViewer = !!viewerUserId && transition.actor_id === viewerUserId;
            // The deadline hand-over (diaper check) keeps the state too: only the holder changes.
            const handOver = transition.action === 'hand_to_admins';
            const rowKeepsState = assignment || reminder || handOver;
            // System rows (the care alert engine) name no person.
            const systemActor = transition.actor_id?.startsWith('system:') ?? false;
            return (
              <div key={transition.id} className="relative flex gap-3 pl-0">
                {/* Dot */}
                <div className="relative z-10 mt-1.5 flex-shrink-0" aria-hidden="true">
                  <div className="h-6 w-6 rounded-full border-2 border-border bg-background flex items-center justify-center">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!rowKeepsState && (
                      <>
                        <StatusBadge
                          tone={getStateTone(transition.from_state)}
                          label={getStateLabel(i18n, transition.from_state ?? '')}
                        />
                        <span className="text-muted-foreground text-xs" aria-hidden="true">→</span>
                        <StatusBadge
                          tone={getStateTone(transition.to_state)}
                          label={getStateLabel(i18n, transition.to_state ?? '')}
                        />
                      </>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {formatDateTime(transition.created_at, i18n.locale)}
                    </span>
                  </div>

                  {reminder ? (
                    <div
                      className="mt-1 text-sm text-muted-foreground"
                      data-testid={`timeline-reminder-${transition.id}`}
                    >
                      {byViewer
                        ? i18n._({
                            id: 'workflow.timeline.remindedByYou',
                            message: 'You reminded {name}',
                            values: { name: remindedName(transition.metadata, userNameMap) },
                          })
                        : i18n._({
                            id: 'workflow.timeline.reminded',
                            message: '{name} reminded by {actor}',
                            values: { name: remindedName(transition.metadata, userNameMap), actor },
                          })}
                    </div>
                  ) : assignment ? (
                    <div
                      className="mt-1 text-sm text-muted-foreground"
                      data-testid={`timeline-assignment-${transition.id}`}
                    >
                      {transition.action === 'assign' && isSelfAssignment(transition)
                        ? byViewer
                          ? i18n._({ id: 'workflow.timeline.takenByYou', message: 'You took the item' })
                          : i18n._({
                              id: 'workflow.timeline.takenBy',
                              message: 'Taken by {actor}',
                              values: { actor },
                            })
                        : transition.action === 'assign'
                        ? byViewer
                          ? i18n._({
                              id: 'workflow.timeline.handedToByYou',
                              message: 'You handed over to {name}',
                              values: { name: assignedToName(transition.metadata, userNameMap) },
                            })
                          : i18n._({
                              id: 'workflow.timeline.handedTo',
                              message: 'Handed to {name} by {actor}',
                              values: { name: assignedToName(transition.metadata, userNameMap), actor },
                            })
                        : byViewer
                        ? i18n._({ id: 'workflow.timeline.personRemovedByYou', message: 'You removed the person' })
                        : i18n._({
                            id: 'workflow.timeline.personRemoved',
                            message: 'Person removed by {actor}',
                            values: { actor },
                          })}
                    </div>
                  ) : (
                    <div className="mt-1 text-sm text-muted-foreground">
                      <span className="font-medium">
                        {getActionLabel(i18n, transition.action ?? '', category)}
                      </span>
                      {transition.actor_id && !systemActor && (
                        <>
                          {' '}
                          {byViewer ? (
                            <span className="text-xs">
                              {i18n._({ id: 'workflow.timeline.byYou', message: 'by you' })}
                            </span>
                          ) : (
                            <>
                              {i18n._({ id: 'workflow.timeline.by', message: 'by' })}{' '}
                              <span className="text-xs">{actor}</span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {transition.comment && (
                    <div className="mt-1 rounded-md bg-muted p-2 text-sm">
                      <span className="text-xs font-medium text-muted-foreground">
                        {i18n._({
                          id: 'workflow.timeline.comment',
                          message: 'Comment',
                        })}
                        :
                      </span>{' '}
                      {transition.comment}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * The new holder's name on an assign row: the name the server stored, else
 * the name map's entry for the stored Clerk id.
 */
function assignedToName(metadata: unknown, userNameMap?: Map<string, string>): string {
  if (typeof metadata !== 'object' || metadata === null) return '';
  const { assigned_to_name: name, assigned_to: clerkUserId } = metadata as {
    assigned_to_name?: unknown;
    assigned_to?: unknown;
  };
  if (typeof name === 'string' && name) return name;
  if (typeof clerkUserId === 'string' && clerkUserId) return userNameMap?.get(clerkUserId) ?? '';
  return '';
}

/** The reminded person's name on a remind row: the stored name, else the name map's entry for the stored id. */
function remindedName(metadata: unknown, userNameMap?: Map<string, string>): string {
  if (typeof metadata !== 'object' || metadata === null) return '';
  const { reminded_name: name, reminded_user_id: clerkUserId } = metadata as {
    reminded_name?: unknown;
    reminded_user_id?: unknown;
  };
  if (typeof name === 'string' && name) return name;
  if (typeof clerkUserId === 'string' && clerkUserId) return userNameMap?.get(clerkUserId) ?? '';
  return '';
}

/** An assign row where the actor handed the item to themselves. */
function isSelfAssignment(transition: WorkflowTransitionResponse): boolean {
  const metadata: unknown = transition.metadata;
  if (typeof metadata !== 'object' || metadata === null || !transition.actor_id) return false;
  return (metadata as { assigned_to?: unknown }).assigned_to === transition.actor_id;
}
